/**
 * The rules behind `ExtendTrialPanel`, with no DOM in sight.
 *
 * `M13` works out a rule nobody asked for — **an already-expired trial is
 * counted from `max(trialEnd, today)`** — and says it out loud in the preview.
 * Adding days to a date that has already passed otherwise produces a "new" end
 * date still in the past: a write that grants nothing and reads on the audit
 * record as though it had. That rule, the five account-level refusals the route
 * pre-empts, the sixth that depends on the typed number rather than on the
 * account, and the two preview sentences are all decisions a test should be
 * able to make without rendering a page, which is why they live here.
 *
 * **The day arithmetic is not one of them.** `extendTrialFrom` in
 * `@atsready/shared` is the estate's single copy, called by this panel and by
 * the route that writes the audit record. Written twice it is written in two
 * date libraries that disagree across a spring-forward — UTC milliseconds on
 * the server, calendar days in the browser's zone here — which crosses a date
 * boundary for any `trialEndDate` in the last hour of a day, so the operator's
 * preview reads one date and the record gets another and nothing notices.
 * `date-fns` is used in this module for `format` and nothing else, which is
 * presentation and carries no such hazard.
 *
 * Every string here is a hard-coded English literal. `admin` has no i18n —
 * no catalogue, no hook, no `t()` — and one translated panel in an otherwise
 * English application is worse than a consistent one.
 */
import { format } from 'date-fns';
import {
  LEGACY_TRIAL_DURATION_DAYS,
  MAX_TRIAL_HORIZON_DAYS,
  ROLE_HIERARCHY,
  extendTrialFrom,
} from '@atsready/shared';
import type { SubscriptionStatus, SubscriptionTier, UserRole } from '@atsready/shared';

/** The smallest extension the form will send. */
export const EXTEND_TRIAL_MIN_DAYS = 1;

/**
 * The largest. `M13`'s own stated purpose for it: "the cap is what stops a
 * mistyped 365 from handing away a year of Pro+". It is a client-side
 * courtesy, never a control — the server enforces its own.
 */
export const EXTEND_TRIAL_MAX_DAYS = 90;

/** The segmented triple `M13` draws beside the number input. */
export const EXTEND_TRIAL_PRESETS = [7, 14, 30] as const;

/** What the number input is seeded with, which is also the middle preset. */
export const EXTEND_TRIAL_DEFAULT_DAYS = 14;

/**
 * `date-fns`' `'MMM d, yyyy'` — the format the *Trial Ends* row above the panel
 * already uses (`UserDetail.tsx`), so the preview and the record read alike.
 */
export const EXTEND_TRIAL_DATE_FORMAT = 'MMM d, yyyy';

/**
 * What the preview says instead of a date when the typed day count is not a
 * whole number inside the bounds. It is not a validation toast: the commit
 * button disables and this sentence takes the preview's place.
 */
export const EXTEND_TRIAL_INVALID_DAYS_NOTE =
  'Enter a whole number of days between 1 and 90.';

/**
 * One day in milliseconds, **derived from `shared`'s own arithmetic rather than
 * declared**.
 *
 * A literal `86_400_000` here would be the third copy of a number this estate
 * has already been bitten by, and the whole point of `RV2-QUAL-9` is that the
 * day length has one definition. Asking `extendTrialFrom` for one day from the
 * epoch answers with whatever `shared` believes a day to be, so the horizon
 * arithmetic below cannot drift from the extension arithmetic above it.
 */
const DAY_MS = extendTrialFrom(new Date(0), 1, new Date(0)).newTrialEndDate.getTime();

/**
 * `from` plus `days`, with no `max(trialEnd, now)` involved.
 *
 * Passing the same instant as both the trial end and the clock makes
 * `extendTrialFrom`'s maximum a no-op — the two are equal — so what comes back
 * is `from + days` and nothing else. `days` may be negative, which is how the
 * horizon's base is reconstructed from an end date.
 */
function addDays(from: Date, days: number): Date {
  return extendTrialFrom(from, days, from).newTrialEndDate;
}

/** Render a date the way the card above the panel renders one. */
export function formatTrialDate(value: Date): string {
  return format(value, EXTEND_TRIAL_DATE_FORMAT);
}

/**
 * What the panel needs to know about a subscription.
 *
 * Structurally a subset of `AdminUserDetail['subscription']`, with one
 * exception: **`trialStartDate` is not on `C4`'s wire.** The horizon's base is
 * the trial's own start, and when the client does not have one it is
 * reconstructed from `trialEndDate - TRIAL_DURATION_DAYS` — never re-based on
 * `now`, which would make the ceiling itself re-basable so 90 + 90 + 90 never
 * terminates. The field is declared optional so the reconstruction has a
 * counterpart to be a reconstruction *of*, and so the rule is the same rule the
 * route runs.
 */
export interface ExtendTrialSubscription {
  id: string;
  status: SubscriptionStatus;
  trialEndDate: string | null;
  trialTier: SubscriptionTier | null;
  isStripeManaged: boolean;
  trialStartDate?: string | null;
}

/** A subscription that passed all five account-level refusals. */
export interface ExtendableSubscription extends ExtendTrialSubscription {
  trialEndDate: string;
  trialTier: SubscriptionTier;
}

/**
 * The five account-level reasons the form is not rendered at all.
 *
 * Each one is a `403`, a `404` or a `400` the route would answer with. The
 * client's refusal is a courtesy — it prevents a confusing toast, not an
 * unauthorised write, which is the server's job and is tested there.
 */
export type ExtendTrialBlock =
  | 'no-subscription'
  | 'below-admin'
  | 'self-action'
  | 'stripe-managed'
  | 'no-trial';

/**
 * The selector's answer, carrying the narrowed row on the one outcome that has
 * one.
 *
 * A plain `ExtendTrialBlock | null` cannot tell the type checker that `null`
 * entails a trial end date and a trial tier, so a panel built on it would need
 * a second guard whose false branch is unreachable — dead code that no test can
 * cover and no reader can justify. The union carries the evidence instead.
 */
export type ExtendTrialGate =
  | { block: ExtendTrialBlock; subscription: null }
  | { block: null; subscription: ExtendableSubscription };

/**
 * The order is the route's order, and that is the whole of its correctness:
 * `self-action` before `stripe-managed` before the trial checks, so a
 * self-extension of a perfectly healthy granted trial still refuses and a
 * Stripe row with a live trial still refuses.
 *
 * The Stripe branch is `C2` with its second conjunct already evaluated — the
 * server sends `stripeSubscriptionId !== null` as `isStripeManaged`, and the
 * `status === 'TRIALING'` half is the client's. `C2` rather than `C1` because
 * this panel extends a *running* trial: `C2` asks "is this trial running, and
 * whose is it?", `C1` asks "did a trial of ours recently end?" — a question
 * about a row the sweep has already moved off `TRIALING`.
 */
export function resolveExtendTrialGate(
  subscription: ExtendTrialSubscription | null,
  role: UserRole | null,
  isSelf: boolean,
): ExtendTrialGate {
  if (subscription === null) {
    return { block: 'no-subscription', subscription: null };
  }
  if (role === null || ROLE_HIERARCHY[role] < ROLE_HIERARCHY['ADMIN']) {
    return { block: 'below-admin', subscription: null };
  }
  if (isSelf) {
    return { block: 'self-action', subscription: null };
  }
  if (subscription.status === 'TRIALING' && subscription.isStripeManaged) {
    return { block: 'stripe-managed', subscription: null };
  }
  const { trialEndDate, trialTier } = subscription;
  if (trialEndDate === null || trialTier === null) {
    return { block: 'no-trial', subscription: null };
  }
  return { block: null, subscription: { ...subscription, trialEndDate, trialTier } };
}

/**
 * Which of the five refusals holds, or `null` when the form may render.
 *
 * The decision itself is `resolveExtendTrialGate`'s; this is the same answer
 * with the narrowed row dropped, for callers that only want to know *whether*.
 */
export function resolveExtendTrialBlock(
  subscription: ExtendTrialSubscription | null,
  role: UserRole | null,
  isSelf: boolean,
): ExtendTrialBlock | null {
  return resolveExtendTrialGate(subscription, role, isSelf).block;
}

/** A trimmed, whole day count inside the bounds, or `null` — the disabled state. */
export function parseExtendTrialDays(raw: string): number | null {
  const value = Number(raw.trim());
  if (!Number.isInteger(value)) {
    return null;
  }
  if (value < EXTEND_TRIAL_MIN_DAYS || value > EXTEND_TRIAL_MAX_DAYS) {
    return null;
  }
  return value;
}

/** Where the added days are counted from, and whether the trial had ended. */
export interface ExtendTrialBase {
  base: Date | null;
  expired: boolean;
}

/**
 * `max(trialEnd, today)`, isolated.
 *
 * A `null` `trialEndDate` is a real case rather than a defensive one — the
 * column is nullable and the card above the panel already guards on it.
 */
export function resolveExtendTrialBase(
  trialEndDate: string | null,
  now: Date,
): ExtendTrialBase {
  if (trialEndDate === null) {
    return { base: null, expired: false };
  }
  const end = new Date(trialEndDate);
  const expired = end.getTime() <= now.getTime();
  return { base: expired ? now : end, expired };
}

/** Whether the horizon refuses this day count, and the largest one it would not. */
export interface ExtendTrialHorizon {
  exceeded: boolean;
  maxDays: number;
}

/**
 * The sixth refusal, kept out of the selector on purpose: it is a property of
 * the **typed number**, not of the account. Rendering a locked block for it
 * would hide the form from an operator whose next keystroke would make the
 * request legal, so the panel keeps the form, disables the commit and says what
 * the maximum is — which is why `maxDays` comes back beside the verdict.
 *
 * `MAX_TRIAL_HORIZON_DAYS` is imported, never declared: a third copy of 180 in
 * a third package is the defect the constant's placement in `shared` exists to
 * stop, and it is in `shared` precisely so this client can pre-empt the
 * refusal rather than discover it as a toast.
 */
export function resolveExtendTrialHorizon(
  subscription: ExtendTrialSubscription,
  days: number,
  now: Date,
): ExtendTrialHorizon {
  if (subscription.trialEndDate === null) {
    return { exceeded: false, maxDays: EXTEND_TRIAL_MAX_DAYS };
  }
  const trialEnd = new Date(subscription.trialEndDate);
  const startedAt = subscription.trialStartDate ?? null;
  // `LEGACY_TRIAL_DURATION_DAYS`, frozen at 14, and NOT `TRIAL_DURATION_DAYS`.
  //
  // This reconstructs a start for rows that never stored one. Those rows were granted under a
  // fourteen-day policy, so fourteen is what they were actually given — and now that the
  // length is a setting, reading the CONFIGURED value here would move every legacy row's
  // ceiling the instant an operator edits the policy. A trial granted in June would become
  // extendable further, or refuse an extension it would have allowed, because of a September
  // setting. Nothing would catch it: the value read is correct in both cases and only its
  // meaning is wrong.
  //
  // The server does the same at `api/src/features/admin/lib/extend-trial.ts`. **Both sides, or
  // the fix is half applied** — this client exists to pre-empt the server's refusal in words,
  // so a client measuring from a different base would refuse extensions the server allows.
  const horizonBase =
    startedAt === null ? addDays(trialEnd, -LEGACY_TRIAL_DURATION_DAYS) : new Date(startedAt);
  const horizon = addDays(horizonBase, MAX_TRIAL_HORIZON_DAYS).getTime();
  const { from, newTrialEndDate } = extendTrialFrom(trialEnd, days, now);
  const room = Math.floor((horizon - from.getTime()) / DAY_MS);
  return {
    exceeded: newTrialEndDate.getTime() > horizon,
    maxDays: Math.min(EXTEND_TRIAL_MAX_DAYS, Math.max(0, room)),
  };
}

/**
 * The two preview sentences — `M13`'s, verbatim, and the only reason this
 * module holds any English.
 *
 * `countedFromToday` is taken by value and never re-derived. The form it
 * replaced was `from === now`, an **object identity** test that is correct only
 * where `now` is the very object assigned to `from`; a client's `now` and its
 * `from` are both freshly minted, so the identity test is permanently `false`
 * and this sentence would never render. After a commit the flag is the
 * server's, which has additionally crossed a JSON boundary.
 */
export function describeExtendTrial(
  trialEndDate: Date,
  newTrialEndDate: Date,
  days: number,
  countedFromToday: boolean,
  now: Date,
): string {
  if (countedFromToday) {
    return `The trial ended ${formatTrialDate(trialEndDate)}, so the ${days} days are counted from today, ${formatTrialDate(now)}.`;
  }
  return `${formatTrialDate(trialEndDate)} → ${formatTrialDate(newTrialEndDate)}. ${days} days added.`;
}

/**
 * The commit button names the number, singular at one day, and falls back to
 * the bare verb while the input is invalid — there is no number to name.
 */
export function describeExtendTrialAction(days: number | null): string {
  if (days === null) {
    return 'Extend trial';
  }
  return `Extend trial by ${days} ${days === 1 ? 'day' : 'days'}`;
}

/**
 * The inline note under the day input when the horizon refuses.
 *
 * It names the ceiling in days rather than as "six months" so the sentence
 * cannot drift from `MAX_TRIAL_HORIZON_DAYS`, which is a policy number somebody
 * may yet change.
 */
export function describeExtendTrialHorizon(maxDays: number): string {
  if (maxDays === 0) {
    return `This trial is already ${MAX_TRIAL_HORIZON_DAYS} days past its start, so it cannot be extended any further.`;
  }
  return `That would take the trial past ${MAX_TRIAL_HORIZON_DAYS} days from its start. The most you can add is ${maxDays} ${maxDays === 1 ? 'day' : 'days'}.`;
}
